import { useRoute } from 'nuxt/app'
import { computed, toValue, type MaybeRefOrGetter } from 'vue'

import { useLearnerApi } from '@courses/learner-api-layer/composables/useLearnerApi'
import { useProduct } from '@courses/product-theme-layer/composables/useProduct'
import { type Module } from '@courses/types-layer/types/module'
import { useLearningExperience } from '@courses/user-post-completion-layer/composables/useLearningExperience'
import { usePostCompletion } from '@courses/user-post-completion-layer/composables/usePostCompletion'
import {
  MIN_VIDEO_PERCENT_TO_AUTO_COMPLETE,
  normalizeAutoCompleteProgressPercentage,
  shouldAutoCompleteLessons,
} from '@courses/user-post-completion-layer/utils/lesson-auto-complete.helper'

import { useAuth } from '@common/auth-layer/composables/useAuth'
import { useCPNavigation } from '@common/navigation-layer/composables/useNavigationContext'

import { usePostsUIStore } from '@courses/post-theme-layer/stores/posts.ui-store'
import type { Post } from '@courses/types-layer/types/post'
import { DEFAULT_CERTIFICATE_THUMBNAIL, DEFAULT_CERTIFICATE_THUMBNAIL_LOCKED } from '../../utility-layer/constants'

import { useModulesQueryStore } from '../stores/modules.query-store'
import { buildModuleTree, buildModuleTreeLookUp, areAllCompletableChildrenComplete } from '../utils/helper'
import type { AutoCompleteNavigationContext, CurriculumChild, UseModulesOptions } from '../types/use-modules'

export type { UseModulesOptions } from '../types/use-modules'

/** Catalog curriculum from `GET /offers/categories/:productId` (pairs with learner `useModules`). */
export { useCatalogOffersModuleTree } from './useCatalogOffersModuleTree'

export const useModules = (courseId: MaybeRefOrGetter<string>, locationId: MaybeRefOrGetter<string>, options?: UseModulesOptions) => {
  const route = useRoute()
  const { CPNavigateTo } = useCPNavigation()
  const { fetchProduct, fetchProductDetail } = useProduct(locationId)
  const { data: product } = fetchProduct(courseId)
  // Detail endpoint carries `customizations.generalSettings`; the library list omits it.
  const { data: productDetail } = fetchProductDetail(courseId)
  const productForCustomizations = computed(() => productDetail.value ?? product.value)
  // Gates settings-driven side-effects so creators with `autoCompleteOn50Percent: false`
  // are respected during the detail-query gap (treat unknown as opt-out).
  const hasResolvedSettings = computed(() => productDetail.value !== undefined)
  const membershipUserId = computed(() => product.value?.membershipUserId ?? '')
  const { generalSettings } = useLearningExperience(productForCustomizations)
  const { store: authStore } = useAuth()
  const { getUnlockedModules } = useLearnerApi(courseId, locationId, {
    fetchUnlockedModules: computed(() => {
      const o = options?.fetchUnlockedModulesEnabled
      return o !== undefined ? toValue(o) : true
    }),
  })
  const { data: unlockedModulesData } = getUnlockedModules()
  const postUnlockedMap = computed(() => unlockedModulesData?.value?.postUnlockedMap || {})
  const categoryUnlockedMap = computed(() => unlockedModulesData?.value?.categoryUnlockedMap || {})

  const {
    fetchAllModulesQuery,
    fetchNextModuleQuery,
    fetchModuleLessonsQuery,
    fetchModulesWithLessonsQuery,
    ensureModulesList,
    ensureModuleLessons,
  } = useModulesQueryStore(courseId, locationId)

  // Disabled contexts (drip/preview/unavailable-product) propagate to learner-progress
  // so this composable doesn't open completed-lessons/unlocked-modules subscriptions
  // when the consuming page already opted out.
  const learnerProgressEnabled = computed(() => {
    const all = options?.fetchAllModulesEnabled
    const unlocked = options?.fetchUnlockedModulesEnabled
    const allEnabled = all !== undefined ? toValue(all) : true
    const unlockedEnabled = unlocked !== undefined ? toValue(unlocked) : true
    return allEnabled && unlockedEnabled
  })

  /** Completion mutation + cached % reads; optimistic-update + invalidation live in the query store. */
  const { markPostAsCompleted, getCompletedPercentage } = usePostCompletion(courseId, locationId, {
    fetchLearnerProgress: learnerProgressEnabled,
  })

  /** In-flight keys (`locationId:productId:lessonId`) to dedupe rapid nav-aways. */
  const completionInFlight = new Set<string>()

  function fetchAllModules() {
    try {
      const modulesQuery = fetchAllModulesQuery(
        computed(() => {
          const o = options?.fetchAllModulesEnabled
          return o !== undefined ? toValue(o) : true
        })
      )
      return modulesQuery
    } catch (error) {
      console.error('Failed to fetch all modules:', error)
      throw error
    }
  }

  function fetchNextModule(moduleId: MaybeRefOrGetter<string>) {
    try {
      const nextModuleQuery = fetchNextModuleQuery(moduleId)
      return nextModuleQuery
    } catch (error) {
      console.error('Failed to fetch next module:', error)
      throw error
    }
  }

  function fetchModuleLessons(moduleId: MaybeRefOrGetter<string>) {
    try {
      const moduleLessonsQuery = fetchModuleLessonsQuery(moduleId)
      return moduleLessonsQuery
    } catch (error) {
      console.error('Failed to fetch module lessons:', error)
      throw error
    }
  }

  function fetchModulesWithLessons(modules: MaybeRefOrGetter<Module[] | undefined>, enabledModuleIds: MaybeRefOrGetter<string[]>) {
    try {
      const modulesWithLessonsQuery = fetchModulesWithLessonsQuery(modules, enabledModuleIds)
      return modulesWithLessonsQuery
    } catch (error) {
      console.error('Failed to fetch modules with lessons:', error)
      throw error
    }
  }

  /** Returns the resolved context, or `null` when the navigation is ineligible. */
  function resolveAutoCompleteNavigationContext(targetPostId: string, targetModuleId: string): AutoCompleteNavigationContext | null {
    // Treat unresolved detail-backed settings as opt-out so a creator's
    // `autoCompleteOn50Percent: false` is honoured during the detail-query gap.
    if (!hasResolvedSettings.value) {
      return null
    }
    if (!shouldAutoCompleteLessons(generalSettings.value)) {
      return null
    }

    const currentLessonId = (route.params.lessonId ?? route.params.postId) as string | undefined
    const currentModuleId = route.params.moduleId as string | undefined
    const productIdValue = toValue(courseId)
    const locationIdValue = toValue(locationId)

    if (isAutoCompleteRouteIneligible(currentLessonId, currentModuleId, productIdValue, locationIdValue, targetPostId, targetModuleId)) {
      return null
    }

    return {
      currentLessonId: currentLessonId as string,
      currentModuleIdFromRoute: currentModuleId as string,
      productIdValue,
      locationIdValue,
    }
  }

  /** True when nav should NOT trigger auto-complete (missing IDs, product mismatch, or same lesson). */
  function isAutoCompleteRouteIneligible(
    currentLessonId: string | undefined,
    currentModuleId: string | undefined,
    productIdValue: string,
    locationIdValue: string,
    targetPostId: string,
    targetModuleId: string
  ): boolean {
    if (!currentLessonId || !currentModuleId) return true
    if (!productIdValue || !locationIdValue) return true
    const routeProductId = route.params.productId as string | undefined
    if (routeProductId && routeProductId !== productIdValue) return true
    if (currentLessonId === targetPostId && currentModuleId === targetModuleId) return true
    return false
  }

  /** Live + cached progress combined; checks the 50% threshold. */
  function hasReachedAutoCompleteThreshold(ctx: AutoCompleteNavigationContext): boolean {
    const postsUi = usePostsUIStore()
    const rawVideoPct = postsUi.getVideoCompletedPercentage()
    const safeRawPct = Number.isFinite(rawVideoPct) ? rawVideoPct : 0

    // Clamps to <100 when preference is off so stale cache can't satisfy the gate.
    const safeAlreadyPct = normalizeAutoCompleteProgressPercentage(getCompletedPercentage(ctx.currentLessonId), generalSettings.value)

    const effectiveWatchPct = Math.max(safeRawPct, safeAlreadyPct)
    return effectiveWatchPct >= MIN_VIDEO_PERCENT_TO_AUTO_COMPLETE && safeAlreadyPct < 100
  }

  /** Captures full nav-away context for background completion; returns `null` when ineligible. */
  function captureVideoAutoCompleteContext(
    targetPostId: string,
    targetModuleId: string
  ): {
    currentLessonId: string
    currentModuleIdFromRoute: string
    productIdValue: string
    locationIdValue: string
    membershipUserIdValue: string
    contactId: string
  } | null {
    const ctx = resolveAutoCompleteNavigationContext(targetPostId, targetModuleId)
    if (!ctx || !hasReachedAutoCompleteThreshold(ctx)) {
      return null
    }
    return {
      ...ctx,
      membershipUserIdValue: membershipUserId.value,
      contactId: authStore.contactId ?? '',
    }
  }

  async function runVideoAutoCompleteAfterNavigation(args: {
    currentLessonId: string
    currentModuleIdFromRoute: string
    productIdValue: string
    locationIdValue: string
    membershipUserIdValue: string
    contactId: string
  }) {
    const { currentLessonId, currentModuleIdFromRoute, productIdValue, locationIdValue, membershipUserIdValue, contactId } = args

    const listData = await ensureModulesList()
    const modules = listData?.modules ?? []
    if (!modules.length) {
      return
    }

    // Fan-out per-module lessons; tolerate transient per-module failures so an unrelated
    // lesson fetch doesn't drop the auto-complete attempt for the watched lesson
    // (`buildModuleTree` already accepts `undefined` entries).
    const lessonSettlements = await Promise.allSettled(modules.map(mod => ensureModuleLessons(mod.id)))
    const lessonResults = lessonSettlements.map(r => (r.status === 'fulfilled' ? r.value : undefined))

    const tree = buildModuleTree(modules, lessonResults)

    // Lookup needed for `currentPost.categoryId` resolution under nested curricula.
    const lookUp = buildModuleTreeLookUp(tree)
    const currentPost = lookUp.get(currentLessonId) as Post | undefined
    const postsUi = usePostsUIStore()
    if (!currentPost || !postsUi.getIsVideo(currentPost)) {
      return
    }

    const inFlightKey = `${locationIdValue}:${productIdValue}:${currentLessonId}`
    if (completionInFlight.has(inFlightKey)) {
      return
    }
    completionInFlight.add(inFlightKey)

    // Prefer post.categoryId so nested Classic clicks don't send the route shell moduleId.
    const moduleIdForCompletion = currentPost.categoryId || currentModuleIdFromRoute

    try {
      // Mutation owns optimistic cache + unlocked-modules invalidation.
      await markPostAsCompleted({
        postId: currentLessonId,
        moduleId: moduleIdForCompletion,
        percentage: 100,
        userId: membershipUserIdValue,
        contactId,
      })
    } finally {
      completionInFlight.delete(inFlightKey)
    }
  }

  async function handlePostRedirection(postId: string, moduleId: string) {
    // Capture context BEFORE navigation: route.params will change once we navigate, and
    // a rejected/blocked nav must NOT trigger a background completion for a lesson the
    // learner never moved away from.
    const capturedCtx = captureVideoAutoCompleteContext(postId, moduleId)

    try {
      await CPNavigateTo(
        {
          name: 'courses-products-modules-lessons',
          params: {
            productId: toValue(courseId),
            moduleId: toValue(moduleId),
            lessonId: toValue(postId),
          },
        },
        true
      )
    } catch (error) {
      console.error('Failed to navigate to course post:', error)
      return
    }

    if (!capturedCtx) return

    void (async () => {
      try {
        await runVideoAutoCompleteAfterNavigation(capturedCtx)
      } catch (error) {
        console.warn('[useModules] Skipping video auto-complete on lesson navigation', error)
      }
    })()
  }

  function addCompletionData(section: any, completedPostsMap: Record<string, any>) {
    const updatedSection = {
      ...section,
    }

    const isCurrentPostCompleted =
      completedPostsMap.value[updatedSection.id || ''] && completedPostsMap.value[updatedSection.id || ''].percentage >= 100

    const isPostPartiallyCompleted =
      completedPostsMap.value[updatedSection.id || ''] && completedPostsMap.value[updatedSection.id || ''].percentage < 100

    if (updatedSection.status === 'locked') {
      const unlockMap = updatedSection.type === 'category' ? categoryUnlockedMap : postUnlockedMap
      const isContentAvailable = unlockMap?.value[updatedSection.id]?.isUnlocked

      if (isContentAvailable && isCurrentPostCompleted) {
        updatedSection.status = 'completed'
      } else if (isContentAvailable) {
        updatedSection.status = ''
      }
    }

    if (updatedSection.isCertificate) {
      updatedSection.status = ''
      updatedSection.posterImage = isCurrentPostCompleted ? DEFAULT_CERTIFICATE_THUMBNAIL : DEFAULT_CERTIFICATE_THUMBNAIL_LOCKED
    }

    const item = {
      ...updatedSection,
      status: isCurrentPostCompleted ? 'completed' : isPostPartiallyCompleted ? 'partially-completed' : updatedSection.status,
      completedPercentage: (completedPostsMap.value[updatedSection.id || ''] as any)?.percentage,
    }

    if (updatedSection.children?.length > 0) {
      item.children = updatedSection.children.map((child: any) => addCompletionData(child, completedPostsMap))

      // Cert posts never carry a completion %; surface them as completed when every
      // completable sibling is done.
      if (item.children.some((c: CurriculumChild) => c.isCertificate) && areAllCompletableChildrenComplete(item.children)) {
        item.children = item.children.map((c: CurriculumChild) =>
          c.isCertificate ? { ...c, status: 'completed', posterImage: DEFAULT_CERTIFICATE_THUMBNAIL } : c
        )
      }
    }
    return item
  }

  return {
    fetchAllModules,
    fetchNextModule,
    fetchModuleLessons,
    handlePostRedirection,
    fetchModulesWithLessons,
    addCompletionData,
  }
}
